import type { ChangeStop } from "@ext/git/core/Diff/logic/changeStops";
import topLevelBlocks, { blockIndexAt } from "@ext/git/core/Diff/logic/topLevelBlocks";
import type { Node } from "prosemirror-model";

export const DIFF_ONLY_CHANGES = {
	contextBlocks: 1,
	minCollapsed: 2,
	previewDelayMs: 550,
};

export interface CollapsedRange {
	firstIndex: number;
	lastIndex: number;
	from: number;
	to: number;
	blocks: number;
}

interface CollapsedRangesProps {
	doc: Node;
	stops: ChangeStop[];
	keepVisible?: number[];
	options?: typeof DIFF_ONLY_CHANGES;
}

const collapsedRanges = (props: CollapsedRangesProps): CollapsedRange[] => {
	const { doc, stops, keepVisible = [], options = DIFF_ONLY_CHANGES } = props;

	const blocks = topLevelBlocks(doc);
	if (!blocks.length) return [];

	// Block 0 is the article title and must never be collapsed.
	const keep = new Set<number>([0]);

	stops.forEach(({ pos }) => {
		const blockIndex = blockIndexAt(blocks, pos);
		for (let shift = -options.contextBlocks; shift <= options.contextBlocks; shift++) {
			const index = blockIndex + shift;
			if (index >= 0 && index < blocks.length) keep.add(index);
		}
	});

	// Move sources sit on unchanged blocks; collapsing them breaks the move pair navigation.
	keepVisible.forEach((pos) => keep.add(blockIndexAt(blocks, pos)));

	const ranges: CollapsedRange[] = [];
	let start: number | null = null;

	const close = (endIndex: number) => {
		if (start === null) return;
		const blocksCount = endIndex - start + 1;
		if (blocksCount >= options.minCollapsed) {
			ranges.push({
				firstIndex: start,
				lastIndex: endIndex,
				from: blocks[start].from,
				to: blocks[endIndex].to,
				blocks: blocksCount,
			});
		}
		start = null;
	};

	blocks.forEach(({ index }) => {
		if (keep.has(index)) {
			close(index - 1);
			return;
		}
		if (start === null) start = index;
	});
	close(blocks.length - 1);

	return ranges;
};

export default collapsedRanges;
